import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiUser,
  FiUsers,
  FiHome,
  FiBookmark,
  FiMapPin,
  FiCamera,
  FiImage,
  FiCalendar,
  FiClock,
  FiMail,
  FiBriefcase,
  FiPhone,
  FiChevronDown,
  FiPower,
  FiPlus,
  FiX,
  FiTrash2,
  FiArrowLeftCircle,
} from "react-icons/fi";
import {
  IoGlobeOutline,
  IoMapOutline,
  IoCashOutline,
  IoMoonOutline,
  IoResizeOutline,
  IoManOutline,
  IoWomanOutline,
} from "react-icons/io5";
import { BsGenderFemale, BsGenderMale } from "react-icons/bs";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { RiGraduationCapLine } from "react-icons/ri";
import { PiBriefcaseLight } from "react-icons/pi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { CiHeart } from "react-icons/ci";
import "./EditProfile.css";
import { useBasicContext } from "../../context/Basic_context";
import { useProfileContext } from "../../context/Profile_context";
import { useLoginContext } from "../../context/Login_context";
import Loader from "../../components/Loader";
import Notification from "../../utils/Notification";
import { useDropzone } from "react-dropzone";
import { IOSDatePicker, IOSTimePicker } from "../../components/Iospicker";
import { Oval } from "react-loader-spinner";

// ============STEPS==================
const STEPS = [
  {
    id: 1,
    label: "Personal Details",
    sub: "Your basic information",
    icon: <FiUser />,
    cls: "step-1",
  },
  {
    id: 2,
    label: "Family Details",
    sub: "Family information",
    icon: <FiUsers />,
    cls: "step-2",
  },
  {
    id: 3,
    label: "Mosal Details",
    sub: "Maternal family details",
    icon: <FiHome />,
    cls: "step-3",
  },
  {
    id: 4,
    label: "Reference",
    sub: "Reference information",
    icon: <FiBookmark />,
    cls: "step-4",
  },
  {
    id: 5,
    label: "Contact Details",
    sub: "Contact information",
    icon: <FiMapPin />,
    cls: "step-5",
  },
];

// ================INPUT-FIELD=================
const InputField = ({
  icon,
  placeholder,
  type = "text",
  value,
  onChange,
  name,
  max,
  disabled,
}) => (
  <div className="ep-input-wrap">
    {icon && <span className="icon">{icon}</span>}
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      name={name}
      max={max}
      disabled={disabled || false}
    />
  </div>
);

// ===============SELECT-FIELD===================
const SelectField = ({
  icon,
  options = [],
  value,
  onChange,
  name,
  loading,
}) => (
  <div className="ep-select-wrap">
    {icon && <span className="icon">{icon}</span>}
    <select
      value={value ?? ""}
      onChange={onChange}
      name={name}
      disabled={loading}
    >
      <option value="">{loading ? "Loading..." : "Select"}</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
    <FiChevronDown className="chevron" />
  </div>
);

// ================LABEL+(*) =============
const FormGroup = ({ label, required, children, full }) => (
  <div className={`ep-form-group${full ? " full" : ""}`}>
    <span className="ep-label">
      {label}
      {required && <span className="required"> *</span>}
    </span>
    {children}
  </div>
);

/* ===============STEP-1====================*/
const Step1 = ({
  data,
  onChange,
  onToggle,
  onProfileDrop,
  onGalleryDrop,
  removeGalleryImage,
  maxDate,
  religionOptions,
  religionLoading,
  maritalStatusOptions,
  maritalStatusLoading,
  casteOptions,
  casteLoading,
  dobPickerOpen,
  tobPickerOpen,
  onDobPickerClose,
  onTobPickerClose,
  onDobPickerOpen,
  onTobPickerOpen,
}) => {
  const { getRootProps: getProfileRoot, getInputProps: getProfileInput } =
    useDropzone({
      onDrop: onProfileDrop,
      accept: { "image/*": [] },
      multiple: false,
    });
  const {
    getRootProps: getGalleryRoot,
    getInputProps: getGalleryInput,
    open: openGallery,
  } = useDropzone({
    onDrop: onGalleryDrop,
    accept: { "image/*": [] },
    maxFiles: 3,
    noClick: true,
    noKeyboard: true,
  });
  const galleryPhotos = Array.isArray(data.galleryPhotos)
    ? data.galleryPhotos
    : [];

  return (
    <>
      <div className="ep-card">
        <div className="ep-card-title">
          <FiCamera /> Profile Photo
        </div>
        <div className="ep-profile-photo-row">
          <div {...getProfileRoot()} className="ep-profile-upload-center">
            <input {...getProfileInput()} />
            <div className="ep-avatar-circle">
              {data.profilePhoto ? (
                <img
                  src={
                    data.profilePhoto instanceof File
                      ? URL.createObjectURL(data.profilePhoto)
                      : data.profile_photo_full_path || data.profilePhoto
                  }
                  alt=""
                />
              ) : (
                <FiCamera />
              )}
            </div>
            <span className="ep-avatar-label">Upload Profile Photo</span>
          </div>
        </div>
        <div className="ep-additional-section">
          <div className="ep-additional-header">
            <FiImage size={18} />
            <span className="ep-additional-title">Additional Photos</span>
            <span className="ep-additional-badge">(Optional - Up to 3)</span>
          </div>
          <div {...getGalleryRoot()}>
            <input {...getGalleryInput()} />
          </div>
          {galleryPhotos.length > 0 ? (
            <div className="ep-gallery-grid">
              {galleryPhotos.map((file, index) => (
                <div key={index} className="ep-gallery-item">
                  <img
                    src={
                      file instanceof File
                        ? URL.createObjectURL(file)
                        : typeof file === "string"
                          ? file
                          : file?.image_url || ""
                    }
                    alt={`gallery-${index + 1}`}
                  />
                  <button
                    className="ep-gallery-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeGalleryImage(index, file);
                    }}
                    type="button"
                  >
                    <FiX size={12} />
                  </button>
                  <div className="ep-gallery-num">{index + 1}</div>
                </div>
              ))}
              {galleryPhotos.length < 3 && (
                <div className="ep-gallery-add-tile" onClick={openGallery}>
                  <FiPlus size={24} />
                  <span>Add Photo</span>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="ep-btn-add-photos"
              onClick={openGallery}
            >
              <FiPlus size={18} />
              Add More Photos
            </button>
          )}
        </div>
      </div>

      <div className="ep-card">
        <div className="ep-card-title">
          <FiUser /> Basic Information
        </div>
        <div className="ep-form-grid cols-2">
          <FormGroup label="Full Name" required full>
            <InputField
              icon={<FiUser />}
              placeholder="Enter full name"
              name="fullName"
              value={data.fullName}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Gender" required full>
            <div className="ep-toggle-group">
              <button
                className={`ep-toggle-btn${data.gender === "Male" ? " active" : ""}`}
                onClick={() => onToggle("gender", "Male")}
              >
                <BsGenderMale className="bs-gender-male" /> Male
              </button>
              <button
                className={`ep-toggle-btn${data.gender === "Female" ? " active" : ""}`}
                onClick={() => onToggle("gender", "Female")}
              >
                <BsGenderFemale className="bs-gender-female" /> Female
              </button>
            </div>
          </FormGroup>
          <FormGroup label="Date of Birth" required>
            <div className="ep-input-wrap" onClick={() => onDobPickerOpen()}>
              <span className="icon">
                <FiCalendar />
              </span>
              <input
                readOnly
                onFocus={(e) => e.target.blur()}
                placeholder="Select date"
                value={data.dob || ""}
              />
            </div>
          </FormGroup>
          <FormGroup label="Time of Birth" required>
            <div className="ep-input-wrap" onClick={() => onTobPickerOpen()}>
              <span className="icon">
                <FiClock />
              </span>
              <input
                readOnly
                onFocus={(e) => e.target.blur()}
                placeholder="Select time"
                value={data.tob || ""}
              />
            </div>
          </FormGroup>
          <FormGroup label="Place of Birth" required full>
            <InputField
              icon={<FiMapPin />}
              placeholder="Enter place"
              name="placeOfBirth"
              value={data.placeOfBirth}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Height" required>
            <InputField
              icon={<IoResizeOutline />}
              placeholder="e.g. 5.1"
              name="height"
              value={data.height}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Weight" required>
            <InputField
              icon={<IoResizeOutline />}
              placeholder="e.g. 58"
              name="weight"
              value={data.weight}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Diet" required full>
            <div className="ep-toggle-group">
              <button
                className={`ep-toggle-btn${data.diet === "Regular" ? " active" : ""}`}
                onClick={() => onToggle("diet", "Regular")}
              >
                Regular
              </button>
              <button
                className={`ep-toggle-btn${data.diet === "Jain" ? " active" : ""}`}
                onClick={() => onToggle("diet", "Jain")}
              >
                Jain
              </button>
            </div>
          </FormGroup>
        </div>
      </div>

      <div className="ep-card">
        <div className="ep-card-title">
          <FiUser /> Additional Details
        </div>
        <div className="ep-form-grid cols-2">
          <FormGroup label="Religion" required>
            <SelectField
              icon={<IoMoonOutline />}
              options={religionOptions}
              loading={religionLoading}
              name="religion"
              value={data.religion}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Marital Status" required>
            <SelectField
              icon={<IoMoonOutline />}
              options={maritalStatusOptions}
              loading={maritalStatusLoading}
              name="maritalStatus"
              value={data.maritalStatus}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Caste" required>
            <SelectField
              icon={<FiUsers />}
              options={casteOptions}
              loading={casteLoading}
              name="caste"
              value={data.caste}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Native" required>
            <InputField
              icon={<FiMapPin />}
              placeholder="Native place"
              name="native"
              value={data.native}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Education" required>
            <InputField
              icon={<RiGraduationCapLine />}
              placeholder="Qualification"
              name="education"
              value={data.education}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Occupation" required>
            <InputField
              icon={<PiBriefcaseLight />}
              placeholder="Job"
              name="occupation"
              value={data.occupation}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Profession" full>
            <InputField
              icon={<PiBriefcaseLight />}
              placeholder="e.g. DR, CA, Advocate..."
              name="profession"
              value={data.profession}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Monthly Income">
            <div className="ep-currency-wrap">
              <span className="ep-currency-prefix">
                <IoCashOutline />
              </span>
              <input
                placeholder="Amount"
                name="monthlyIncome"
                value={data.monthlyIncome}
                onChange={onChange}
              />
            </div>
          </FormGroup>
          <FormGroup label="Yearly Income">
            <div className="ep-currency-wrap">
              <span className="ep-currency-prefix">
                <IoCashOutline />
              </span>
              <input
                placeholder="Amount"
                name="yearlyIncome"
                value={data.yearlyIncome}
                onChange={onChange}
              />
            </div>
          </FormGroup>
          <FormGroup label="Office Address" full>
            <InputField
              icon={<HiOutlineBuildingOffice2 />}
              placeholder="Office location"
              name="officeAddress"
              value={data.officeAddress}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Hobbies" full>
            <div className="ep-textarea-wrap">
              <span>
                <CiHeart size={20} style={{ strokeWidth: "0.8" }} />
              </span>
              <textarea
                placeholder="Your interests..."
                name="hobbies"
                value={data.hobbies}
                onChange={onChange}
                rows={2}
              />
            </div>
          </FormGroup>
          <FormGroup label="Email" required full>
            <InputField
              icon={<FiMail />}
              type="email"
              placeholder="your@email.com"
              name="email"
              disabled
              value={data.email}
              onChange={onChange}
            />
          </FormGroup>
        </div>
      </div>
      {dobPickerOpen && (
        <IOSDatePicker
          value={data.dob ? new Date(data.dob) : null}
          maxDate={new Date(maxDate)}
          onChange={(d) => {
            if (d) {
              const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              onChange({ target: { name: "dob", value: local } });
            }
            onDobPickerClose();
          }}
          onClose={onDobPickerClose}
        />
      )}
      {tobPickerOpen && (
        <IOSTimePicker
          value={
            data.tob
              ? (() => {
                  const d = new Date();
                  const [h, m] = data.tob.split(":");
                  d.setHours(+h, +m, 0);
                  return d;
                })()
              : null
          }
          onChange={(t) => {
            if (t)
              onChange({
                target: {
                  name: "tob",
                  value: `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`,
                },
              });
            onTobPickerClose();
          }}
          onClose={onTobPickerClose}
          title="Time of Birth"
        />
      )}
    </>
  );
};

/*==============STEP-2=======================*/
const Step2 = ({
  data,
  onChange,
  brothers,
  sisters,
  onBrotherChange,
  onSisterChange,
  onDeleteBrother,
  onDeleteSister,
  onBrotherCountChange,
  onSisterCountChange,
}) => {
  const LivingStatus = [
    { id: "alive", name: "Alive" },
    { id: "late", name: "Late" },
  ];
  const livingTogetherData = [
    { id: 1, name: "Yes" },
    { id: 0, name: "No" },
  ];

  const brotherVal = String(Math.min(brothers.length, 5));
  const sisterVal = String(Math.min(sisters.length, 5));
  return (
    <>
      {/* Father */}
      <div className="ep-card">
        <div className="ep-card-title">
          <FiUser /> Father's Details
        </div>
        <div className="ep-form-grid cols-2">
          <FormGroup label="Status" required full>
            <SelectField
              icon={<FiHome />}
              options={LivingStatus.map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              name="fatherStatus"
              value={data.fatherStatus}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Father's Name" required full>
            <InputField
              icon={<FiUser />}
              placeholder="Enter name"
              name="fatherName"
              value={data.fatherName}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Occupation">
            <InputField
              icon={<PiBriefcaseLight />}
              placeholder="Occupation"
              name="fatherOccupation"
              value={data.fatherOccupation}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Contact">
            <InputField
              icon={<FiPhone />}
              placeholder="Phone number"
              name="fatherContact"
              value={data.fatherContact}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Living Together?" full>
            <SelectField
              icon={<FiHome />}
              options={livingTogetherData.map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              name="fatherLiving"
              value={data.fatherLiving}
              onChange={onChange}
            />
          </FormGroup>
        </div>
      </div>

      {/* Mother */}
      <div className="ep-card">
        <div className="ep-card-title">
          <FiUser /> Mother's Details
        </div>
        <div className="ep-form-grid cols-2">
          <FormGroup label="Status" required full>
            <SelectField
              icon={<FiHome />}
              options={LivingStatus.map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              name="motherStatus"
              value={data.motherStatus}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Mother's Name" required full>
            <InputField
              icon={<FiUser />}
              placeholder="Enter name"
              name="motherName"
              value={data.motherName}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Occupation">
            <InputField
              icon={<PiBriefcaseLight />}
              placeholder="Occupation"
              name="motherOccupation"
              value={data.motherOccupation}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Contact">
            <InputField
              icon={<FiPhone />}
              placeholder="Phone number"
              name="motherContact"
              value={data.motherContact}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup label="Living Together?" full>
            <SelectField
              icon={<FiHome />}
              options={livingTogetherData.map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              name="motherLiving"
              value={data.motherLiving}
              onChange={onChange}
            />
          </FormGroup>
        </div>
      </div>

      {/* Siblings — dropdown select (original style) */}
      <div className="ep-card">
        <div className="ep-card-title">
          <FiUsers /> Siblings
        </div>
        <div className="ep-form-grid cols-2">
          <FormGroup label="Brothers">
            <SelectField
              icon={<IoManOutline size={22} />}
              options={["0", "1", "2", "3", "4", "5"]}
              value={brotherVal}
              onChange={(e) => onBrotherCountChange(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Sisters">
            <SelectField
              icon={<IoWomanOutline size={22} />}
              options={["0", "1", "2", "3", "4", "5"]}
              value={sisterVal}
              onChange={(e) => onSisterCountChange(e.target.value)}
            />
          </FormGroup>
        </div>
      </div>

      {/* Brother Cards */}
      {brothers.map((brother, i) => (
        <div
          className="ep-card"
          key={brother._key}
          style={{ borderLeft: "4px solid var(--theme1)" }}
        >
          <div className="ep-card-title Siblings ">
            <span>
              <FiUsers /> Brother {i + 1}
            </span>
            <button
              type="button"
              onClick={() => onDeleteBrother(i)}
              className="remove-btn"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
          <div className="ep-form-grid cols-2">
            <FormGroup label="Type">
              <SelectField
                options={["Elder", "Younger", "Twin"]}
                value={brother.type || ""}
                onChange={(e) => onBrotherChange(i, "type", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Name">
              <InputField
                icon={<FiUser />}
                placeholder="Enter name"
                value={brother.name || ""}
                onChange={(e) => onBrotherChange(i, "name", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Occupation">
              <InputField
                icon={<FiBriefcase />}
                placeholder="Occupation"
                value={brother.occupation || ""}
                onChange={(e) =>
                  onBrotherChange(i, "occupation", e.target.value)
                }
              />
            </FormGroup>
            <FormGroup label="Living With?">
              <SelectField
                options={["Yes", "No"]}
                value={brother.living || ""}
                onChange={(e) => onBrotherChange(i, "living", e.target.value)}
              />
            </FormGroup>
          </div>
        </div>
      ))}

      {/* Sister Cards */}
      {sisters.map((sister, i) => (
        <div
          className="ep-card"
          key={sister._key}
          style={{ borderLeft: "4px solid var(--pink,#e91e8c)" }}
        >
          <div className="ep-card-title Siblings">
            <span>
              <FiUsers /> Sister {i + 1}
            </span>
            <button
              type="button"
              onClick={() => onDeleteSister(i)}
              className="remove-btn"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
          <div className="ep-form-grid cols-2">
            <FormGroup label="Type">
              <SelectField
                options={["Elder", "Younger", "Twin"]}
                value={sister.type || ""}
                onChange={(e) => onSisterChange(i, "type", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Name">
              <InputField
                icon={<FiUser />}
                placeholder="Enter name"
                value={sister.name || ""}
                onChange={(e) => onSisterChange(i, "name", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Marital Status">
              <SelectField
                options={["Married", "Unmarried"]}
                value={sister.marital || ""}
                onChange={(e) => onSisterChange(i, "marital", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Occupation">
              <InputField
                icon={<FiBriefcase />}
                placeholder="Occupation"
                value={sister.occupation || ""}
                onChange={(e) =>
                  onSisterChange(i, "occupation", e.target.value)
                }
              />
            </FormGroup>
            <FormGroup label="Living With?" full>
              <SelectField
                options={["Yes", "No"]}
                value={sister.living || ""}
                onChange={(e) => onSisterChange(i, "living", e.target.value)}
              />
            </FormGroup>
          </div>
        </div>
      ))}
    </>
  );
};

/*=============STEP-3========================*/
const Step3 = ({
  data,
  onChange,
  onMosalCountryChange,
  onMosalStateChange,
  countryOptions,
  countryLoading,
  mosalStateOptions,
  mosalStateLoading,
  mosalCityOptions,
  mosalCityLoading,
}) => (
  <div className="ep-card">
    <div className="ep-card-title">
      <FiHome /> Maternal Family Details
    </div>
    <div className="ep-form-grid cols-2">
      <FormGroup label="Mosal Name" required>
        <InputField
          icon={<FiUser />}
          placeholder="Mosal name"
          name="mosalName"
          value={data.mosalName}
          onChange={onChange}
        />
      </FormGroup>
      <FormGroup label="Contact" required>
        <InputField
          icon={<FiPhone />}
          placeholder="Phone"
          name="mosalContact"
          value={data.mosalContact}
          onChange={onChange}
        />
      </FormGroup>
      <FormGroup label="Country">
        <SelectField
          icon={<IoGlobeOutline />}
          options={countryOptions}
          loading={countryLoading}
          name="mosalCountry"
          value={data.mosalCountry}
          onChange={onMosalCountryChange}
        />
      </FormGroup>
      <FormGroup label="State">
        <SelectField
          icon={<IoMapOutline />}
          options={mosalStateOptions}
          loading={mosalStateLoading}
          name="mosalState"
          value={data.mosalState}
          onChange={onMosalStateChange}
        />
      </FormGroup>
      <FormGroup label="City" full>
        <SelectField
          icon={<FiMapPin />}
          options={mosalCityOptions}
          loading={mosalCityLoading}
          name="mosalCity"
          value={data.mosalCity}
          onChange={onChange}
        />
      </FormGroup>
      <FormGroup label="Address" full>
        <InputField
          icon={<FiHome />}
          placeholder="Full address"
          name="mosalAddress"
          value={data.mosalAddress}
          onChange={onChange}
        />
      </FormGroup>
    </div>
  </div>
);

/*==============STEP-4=======================*/
const Step4 = ({ data, onChange }) => (
  <div className="ep-card">
    <div className="ep-card-title">
      <FiBookmark /> Reference Information
    </div>
    <div className="ep-form-grid cols-2">
      <FormGroup label="Reference Name" required full>
        <InputField
          icon={<FiUser />}
          placeholder="Full name"
          name="refName"
          value={data.refName}
          onChange={onChange}
        />
      </FormGroup>
      <FormGroup label="Contact Number" required full>
        <InputField
          icon={<FiPhone />}
          placeholder="Phone number"
          name="refContact"
          value={data.refContact}
          onChange={onChange}
        />
      </FormGroup>
    </div>
  </div>
);

/*==============STEP-5=======================*/
const Step5 = ({
  data,
  onChange,
  onCountryChange,
  onStateChange,
  countryOptions,
  countryLoading,
  stateOptions,
  stateLoading,
  cityOptions,
  cityLoading,
}) => (
  <div className="ep-card">
    <div className="ep-card-title">
      <FiMapPin /> Contact Information
    </div>
    <div className="ep-form-grid cols-2">
      <FormGroup label="Country" required>
        <SelectField
          icon={<IoGlobeOutline />}
          options={countryOptions}
          loading={countryLoading}
          name="country"
          value={data.country}
          onChange={onCountryChange}
        />
      </FormGroup>
      <FormGroup label="State" required>
        <SelectField
          icon={<IoMapOutline />}
          options={stateOptions}
          loading={stateLoading}
          name="state"
          value={data.state}
          onChange={onStateChange}
        />
      </FormGroup>
      <FormGroup label="City" required full>
        <SelectField
          icon={<FiMapPin />}
          options={cityOptions}
          loading={cityLoading}
          name="city"
          value={data.city}
          onChange={onChange}
        />
      </FormGroup>
      <FormGroup label="Complete Address" required full>
        <div className="ep-textarea-wrap">
          <span>
            <FiHome />
          </span>
          <textarea
            placeholder="Full address..."
            name="address"
            value={data.address}
            onChange={onChange}
            rows={3}
          />
        </div>
      </FormGroup>
    </div>
  </div>
);

/* ── MAIN ── */
let _k = 0;
const uid = () => `_${++_k}`;
const newBrother = () => ({
  _key: uid(),
  id: null,
  name: "",
  type: "",
  occupation: "",
  living: "",
});
const newSister = () => ({
  _key: uid(),
  id: null,
  name: "",
  type: "",
  occupation: "",
  living: "",
  marital: "",
});

/*==============EDIT-PROFILE=======================*/
const EditProfile = ({ isProfileComplete }) => {
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  )
    .toISOString()
    .split("T")[0];
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  const [brothers, setBrothers] = useState([]);
  const [sisters, setSisters] = useState([]);
  const [showDobPicker, setShowDobPicker] = useState(false);
  const [showTobPicker, setShowTobPicker] = useState(false);
  const siblingInitialized = useRef(false);

  const {
    GetProfileDate,
    UpdateProfile,
    DeleteBrother,
    DeleteSister,
    DeleteOtherImage,
    profile_data,
    profile_loading,
    update_loading,
  } = useProfileContext();
  const { setLogout } = useLoginContext();
  const {
    cast_loading,
    cast_data,
    coutry_loading,
    country_data,
    religion_loading,
    religion_data,
    maritialstatus_loading,
    maritialstatus_data,
    GetCasteApi,
    GetCountryApi,
    GetStateApi,
    GetCityApi,
    GetReligionApi,
    GetMaritalStatusApi,
  } = useBasicContext();

  const [mosalStateData, setMosalStateData] = useState([]);
  const [mosalCityData, setMosalCityData] = useState([]);
  const [mosalStateLoading, setMosalStateLoading] = useState(false);
  const [mosalCityLoading, setMosalCityLoading] = useState(false);

  const [contactStateData, setContactStateData] = useState([]);
  const [contactCityData, setContactCityData] = useState([]);
  const [contactStateLoading, setContactStateLoading] = useState(false);
  const [contactCityLoading, setContactCityLoading] = useState(false);
  const handleLogout = () => setLogout(navigate);

  useEffect(() => {
    GetCasteApi();
    GetCountryApi();
    GetReligionApi();
    GetMaritalStatusApi();
    GetProfileDate();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!profile_data || !Object.keys(profile_data).length) return;
    const d = profile_data;
    if (!siblingInitialized.current) {
      siblingInitialized.current = true;

      if (d.brothers && Array.isArray(d.brothers) && d.brothers.length) {
        setBrothers(
          d.brothers.map((b) => ({
            _key: uid(),
            id: b.id ?? null,
            name: b.brother_name ?? "",
            type: b.brother_type
              ? b.brother_type.charAt(0).toUpperCase() + b.brother_type.slice(1)
              : "",
            occupation: b.brother_occupation ?? "",
            living: b.brother_living === "1" ? "Yes" : "No",
          })),
        );
      } else {
        setBrothers(Array.from({ length: Number(d.brother ?? 0) }, newBrother));
      }

      if (d.sisters && Array.isArray(d.sisters) && d.sisters.length) {
        setSisters(
          d.sisters.map((s) => ({
            _key: uid(),
            id: s.id ?? null,
            name: s.sister_name ?? "",
            type: s.sister_type
              ? s.sister_type.charAt(0).toUpperCase() + s.sister_type.slice(1)
              : "",
            occupation: s.sister_occupation ?? "",
            living: s.sister_living === "1" ? "Yes" : "No",
            marital: s.sister_marital_status ?? "",
          })),
        );
      } else {
        setSisters(Array.from({ length: Number(d.sister ?? 0) }, newSister));
      }
    }
    setFormData((prev) => ({
      ...prev,
      fullName: d.name ?? d.full_name ?? prev.fullName,
      gender: d.gender ?? prev.gender,
      dob: d.dob ?? d.date_of_birth ?? prev.dob,
      tob: d.tob ?? d.birth_time ?? d.time_of_birth ?? prev.tob,
      placeOfBirth: d.birth_place ?? d.place_of_birth ?? prev.placeOfBirth,
      height: d.height ?? prev.height,
      weight: d.weight ?? prev.weight,
      diet: d.diet ?? prev.diet,
      religion: d.religion_id ?? d.religion ?? prev.religion,
      maritalStatus:
        d.marital_status_id ?? d.marital_status ?? prev.maritalStatus,
      caste: d.cast_id,
      native: d.native ?? prev.native,
      education: d.education ?? prev.education,
      occupation: d.job,
      profession: d.profession ?? prev.profession,
      monthlyIncome: d.income_monthly || "",
      yearlyIncome: d.income_yearly || "",
      officeAddress: d.office_address ?? d.officeAddress ?? prev.officeAddress,
      hobbies: d.hobby || "",
      email: d.email ?? prev.email,
      profilePhoto: d.profile_photo ?? d.photo ?? prev.profilePhoto,
      galleryPhotos: d.other_images ?? d.gallery_photos ?? prev.galleryPhotos,
      fatherStatus: d.father_status ?? prev.fatherStatus,
      fatherName: d.father_name ?? prev.fatherName,
      fatherOccupation: d.father_occupation ?? prev.fatherOccupation,
      fatherContact: d.father_contact_no || "",
      fatherLiving: d.father_living ?? "",
      motherStatus: d.mother_status ?? prev.motherStatus,
      motherName: d.mother_name ?? prev.motherName,
      motherOccupation: d.mother_occupation ?? prev.motherOccupation,
      motherContact: d.mother_contact_no ?? prev.motherContact,
      motherLiving: d.mother_living ?? "",
      mosalName: d.mosal_name ?? prev.mosalName,
      mosalContact: d.mosal_number,
      mosalCountry: d.mosal_country_id ?? d.mosal_country ?? prev.mosalCountry,
      mosalState: d.mosal_state_id ?? d.mosal_state ?? prev.mosalState,
      mosalCity: d.mosal_city_id ?? d.mosal_city ?? prev.mosalCity,
      mosalAddress: d.mosal_address ?? prev.mosalAddress,
      refName: d.reference_name ?? d.ref_name ?? prev.refName,
      refContact: d.reference_number,
      country: d.country_id ?? d.country ?? prev.country,
      state: d.state_id ?? d.state ?? prev.state,
      city: d.city_id ?? d.city ?? prev.city,
      address: d.address ?? prev.address,
      profile_photo_full_path: d?.photo_url,
    }));

    const mc = d.mosal_country_id ?? d.mosal_country;
    const ms = d.mosal_state_id ?? d.mosal_state;
    const cc = d.country_id ?? d.country;
    const cs = d.state_id ?? d.state;
    const loadLocationData = async () => {
      if (mc) {
        const data = await GetStateApi({ country_id: mc });
        setMosalStateData(data || []);
      }
      if (ms) {
        const data = await GetCityApi({ state_id: ms });
        setMosalCityData(data || []);
      }
      if (cc) {
        const data = await GetStateApi({ country_id: cc });
        setContactStateData(data || []);
      }
      if (cs) {
        const data = await GetCityApi({ state_id: cs });
        setContactCityData(data || []);
      }
    };

    loadLocationData();
  }, [profile_data]); // eslint-disable-line

  const religionOptions = religion_data.map((r) => ({
    value: r.id ?? r,
    label: r.name ?? r.title ?? r,
  }));
  const maritalStatusOptions = maritialstatus_data.map((m) => ({
    value: m.id ?? m,
    label: m.name ?? m.title ?? m,
  }));
  const casteOptions = cast_data.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? c,
  }));
  const countryOptions = country_data.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? c,
  }));

  const stateOptions = contactStateData.map((s) => ({
    value: s.id ?? s,
    label: s.name ?? s.title ?? s,
  }));
  const cityOptions = contactCityData.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? c,
  }));
  const mosalStateOptions = mosalStateData.map((s) => ({
    value: s.id ?? s,
    label: s.name ?? s.title ?? s,
  }));
  const mosalCityOptions = mosalCityData.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? c,
  }));

  const [formData, setFormData] = useState({
    fullName: "",
    gender: "",
    dob: "",
    tob: "",
    placeOfBirth: "",
    height: "",
    weight: "",
    diet: "",
    religion: "",
    maritalStatus: "",
    caste: "",
    native: "",
    education: "",
    occupation: "",
    profession: "",
    monthlyIncome: "",
    yearlyIncome: "",
    officeAddress: "",
    hobbies: "",
    email: "",
    profilePhoto: null,
    galleryPhotos: [],
    fatherStatus: "",
    fatherName: "",
    fatherOccupation: "",
    fatherContact: "",
    fatherLiving: "",
    motherStatus: "",
    motherName: "",
    motherOccupation: "",
    motherContact: "",
    motherLiving: "",
    mosalName: "",
    mosalContact: "",
    mosalCountry: "",
    mosalState: "",
    mosalCity: "",
    mosalAddress: "",
    refName: "",
    refContact: "",
    country: "",
    state: "",
    city: "",
    address: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const phoneFields = [
      "fatherContact",
      "motherContact",
      "mosalContact",
      "refContact",
    ];
    if (phoneFields.includes(name) && !/^[0-9]{0,10}$/.test(value)) return;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleToggle = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));

  /* ── Brother handlers ── */
  const handleBrotherCountChange = (val) => {
    const newCount = Math.min(parseInt(val) || 0, 5);
    const cur = brothers.length;
    if (newCount > cur) {
      setBrothers((p) => [
        ...p,
        ...Array.from({ length: newCount - cur }, newBrother),
      ]);
    } else if (newCount < cur) {
      setBrothers((p) => p.slice(0, newCount));
    }
  };

  const handleBrotherChange = (i, field, val) =>
    setBrothers((p) =>
      p.map((b, idx) => (idx === i ? { ...b, [field]: val } : b)),
    );

  const handleDeleteBrother = (i) => {
    const id = brothers[i]?.id;
    if (id) {
      const fd = new FormData();
      fd.append("id", id);
      DeleteBrother(fd);
    }
    setBrothers((p) => p.filter((_, idx) => idx !== i));
  };

  /* ── Sister handlers ── */
  const handleSisterCountChange = (val) => {
    const newCount = Math.min(parseInt(val) || 0, 5);
    const cur = sisters.length;
    if (newCount > cur) {
      setSisters((p) => [
        ...p,
        ...Array.from({ length: newCount - cur }, newSister),
      ]);
    } else if (newCount < cur) {
      setSisters((p) => p.slice(0, newCount));
    }
  };

  const handleSisterChange = (i, field, val) =>
    setSisters((p) =>
      p.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)),
    );

  const handleDeleteSister = (i) => {
    const id = sisters[i]?.id;
    if (id) {
      const fd = new FormData();
      fd.append("id", id);
      DeleteSister(fd);
    }
    setSisters((p) => p.filter((_, idx) => idx !== i));
  };

  const handleCountryChange = (e) => {
    const v = e.target.value;
    setFormData((p) => ({ ...p, country: v, state: "", city: "" }));
    setContactCityData([]);
    if (v) {
      setContactStateLoading(true);
      GetStateApi({ country_id: v })
        .then((data) => setContactStateData(data || []))
        .finally(() => setContactStateLoading(false));
    }
  };
  const handleStateChange = (e) => {
    const v = e.target.value;
    setFormData((p) => ({ ...p, state: v, city: "" }));
    if (v) {
      setContactCityLoading(true);
      GetCityApi({ state_id: v })
        .then((data) => setContactCityData(data || []))
        .finally(() => setContactCityLoading(false));
    }
  };
  const handleMosalCountryChange = (e) => {
    const v = e.target.value;
    setFormData((p) => ({
      ...p,
      mosalCountry: v,
      mosalState: "",
      mosalCity: "",
    }));
    setMosalCityData([]);
    if (v) {
      setMosalStateLoading(true);
      GetStateApi({ country_id: v })
        .then((data) => setMosalStateData(data || []))
        .finally(() => setMosalStateLoading(false));
    }
  };
  const handleMosalStateChange = (e) => {
    const v = e.target.value;
    setFormData((p) => ({ ...p, mosalState: v, mosalCity: "" }));
    if (v) {
      setMosalCityLoading(true);
      GetCityApi({ state_id: v })
        .then((data) => setMosalCityData(data || []))
        .finally(() => setMosalCityLoading(false));
    }
  };
  const onProfileDrop = useCallback(
    (files) => setFormData((p) => ({ ...p, profilePhoto: files[0] })),
    [],
  );
  const onGalleryDrop = useCallback((files) => {
    setFormData((p) => {
      const cur = Array.isArray(p.galleryPhotos) ? p.galleryPhotos : [];
      if (cur.length + files.length > 3) {
        Notification("error", "Maximum three images allowed");
        return p;
      }
      return { ...p, galleryPhotos: [...cur, ...files].slice(0, 3) };
    });
  }, []);

  const removeGalleryImage = (index, photo) => {
    if (photo && !(photo instanceof File) && photo?.id) {
      const fd = new FormData();
      fd.append("id", photo.id);
      DeleteOtherImage(fd, navigate);
      return;
    }
    setFormData((p) => ({
      ...p,
      galleryPhotos: (p.galleryPhotos || []).filter((_, i) => i !== index),
    }));
  };

  const validateFields = (fields) => {
    for (let f of fields) {
      if (!formData[f.key]) {
        Notification("warning", `${f.label} is required`);
        return false;
      }
    }
    return true;
  };

  const goNext = () => {
    if (
      currentStep === 1 &&
      !validateFields([
        { key: "profilePhoto", label: "Profile Photo" },
        { key: "fullName", label: "Full Name" },
        { key: "gender", label: "Gender" },
        { key: "dob", label: "Date of Birth" },
        { key: "tob", label: "Time of Birth" },
        { key: "placeOfBirth", label: "Place of Birth" },
        { key: "height", label: "Height" },
        { key: "weight", label: "Weight" },
        { key: "diet", label: "Diet" },
        { key: "religion", label: "Religion" },
        { key: "maritalStatus", label: "Marital Status" },
        { key: "caste", label: "Caste" },
        { key: "native", label: "Native" },
        { key: "education", label: "Education" },
        { key: "occupation", label: "Occupation" },
      ])
    )
      return;
    if (
      currentStep === 2 &&
      !validateFields([
        { key: "fatherStatus", label: "Father Status" },
        { key: "fatherName", label: "Father Name" },
        { key: "motherStatus", label: "Mother Status" },
        { key: "motherName", label: "Mother Name" },
      ])
    )
      return;
    if (
      currentStep === 3 &&
      !validateFields([
        { key: "mosalName", label: "Mosal Name" },
        { key: "mosalContact", label: "Mosal Contact" },
      ])
    )
      return;
    if (
      currentStep === 4 &&
      !validateFields([
        { key: "refName", label: "Reference Name" },
        { key: "refContact", label: "Reference Contact" },
      ])
    )
      return;
    setCurrentStep((s) => Math.min(s + 1, 5));
  };

  const goBack = () =>
    currentStep === 1 ? navigate(-1) : setCurrentStep((s) => s - 1);

  const handleFinalSubmit = () => {
    if (!formData.dob || new Date(formData.dob) > new Date(maxDate)) {
      Notification(
        "warning",
        "Registration is only allowed for ages 18 and above.",
      );
      return;
    }
    if (
      !validateFields([
        { key: "profilePhoto", label: "Profile Photo" },
        { key: "fullName", label: "Full Name" },
        { key: "gender", label: "Gender" },
        { key: "dob", label: "Date of Birth" },
        { key: "tob", label: "Time of Birth" },
        { key: "placeOfBirth", label: "Place of Birth" },
        { key: "height", label: "Height" },
        { key: "weight", label: "Weight" },
        { key: "diet", label: "Diet" },
        { key: "religion", label: "Religion" },
        { key: "maritalStatus", label: "Marital Status" },
        { key: "caste", label: "Caste" },
        { key: "native", label: "Native" },
        { key: "education", label: "Education" },
        { key: "occupation", label: "Occupation" },
        { key: "fatherStatus", label: "Father Status" },
        { key: "fatherName", label: "Father Name" },
        { key: "motherStatus", label: "Mother Status" },
        { key: "motherName", label: "Mother Name" },
        { key: "mosalName", label: "Mosal Name" },
        { key: "mosalContact", label: "Mosal Contact" },
        { key: "refName", label: "Reference Name" },
        { key: "refContact", label: "Reference Contact" },
        { key: "country", label: "Country" },
        { key: "state", label: "State" },
        { key: "city", label: "City" },
        { key: "address", label: "Address" },
      ])
    )
      return;

    const fd = new FormData();
    fd.append("name", formData.fullName);
    fd.append(
      "gender",
      formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1),
    );
    fd.append("dob", formData.dob);
    fd.append("place_of_birth", formData.placeOfBirth);
    fd.append("time_of_birth", formData.tob);
    fd.append("height", formData.height);
    fd.append("weight", formData.weight);
    fd.append("diet", formData.diet);
    fd.append("cast_id", formData.caste);
    fd.append("marital_status_id", formData.maritalStatus);
    fd.append("religion_id", formData.religion);
    fd.append("education", formData.education);
    fd.append("job", formData.occupation);
    fd.append("profession", formData.profession);
    fd.append("hobby", formData.hobbies);
    fd.append("email", formData.email);
    fd.append("income_monthly", formData.monthlyIncome);
    fd.append("income_yearly", formData.yearlyIncome);
    fd.append("office_address", formData.officeAddress);
    fd.append("native", formData.native);

    if (formData.profilePhoto && typeof formData.profilePhoto !== "string")
      fd.append("photo", formData.profilePhoto);
    (formData.galleryPhotos || []).forEach((p, i) => {
      if (p instanceof File) fd.append(`other_photos[${i}]`, p);
    });

    fd.append("father_status", formData.fatherStatus);
    fd.append("father_name", formData.fatherName);
    fd.append("father_occupation", formData.fatherOccupation);
    fd.append("father_contact_no", formData.fatherContact);
    fd.append("father_living", formData.fatherLiving);
    fd.append("mother_status", formData.motherStatus);
    fd.append("mother_name", formData.motherName);
    fd.append("mother_occupation", formData.motherOccupation);
    fd.append("mother_contact_no", formData.motherContact);
    fd.append("mother_living", formData.motherLiving);

    fd.append("brother", brothers.length);
    fd.append("sister", sisters.length);

    fd.append("mosal_name", formData.mosalName);
    fd.append("mosal_number", formData.mosalContact);
    fd.append("mosal_country_id", formData.mosalCountry);
    fd.append("mosal_state_id", formData.mosalState);
    fd.append("mosal_city_id", formData.mosalCity);
    fd.append("mosal_address", formData.mosalAddress);
    fd.append("reference_name", formData.refName);
    fd.append("reference_number", formData.refContact);
    fd.append("country_id", formData.country);
    fd.append("state_id", formData.state);
    fd.append("city_id", formData.city);
    fd.append("address", formData.address);

    brothers.forEach((b, i) => {
      fd.append(`brother_name[${i}]`, b.name || "");
      fd.append(`brother_type[${i}]`, b.type || "");
      fd.append(`brother_occupation[${i}]`, b.occupation || "");
      fd.append(`brother_living[${i}]`, b.living === "Yes" ? 1 : 0);
    });
    sisters.forEach((s, i) => {
      fd.append(`sister_name[${i}]`, s.name || "");
      fd.append(`sister_type[${i}]`, s.type || "");
      fd.append(`sister_occupation[${i}]`, s.occupation || "");
      fd.append(`sister_living[${i}]`, s.living === "Yes" ? 1 : 0);
      fd.append(`sister_marital_status[${i}]`, s.marital || "");
    });

    UpdateProfile(fd, navigate);
  };

  const progressPct = ((currentStep - 1) / 4) * 100;
  const step = STEPS[currentStep - 1];
  const actionMode =
    currentStep === 1
      ? "one-btn"
      : currentStep === 5 && !isProfileComplete
        ? "three-btns"
        : "two-btns";

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1
            data={formData}
            onChange={handleChange}
            onToggle={handleToggle}
            onProfileDrop={onProfileDrop}
            onGalleryDrop={onGalleryDrop}
            removeGalleryImage={removeGalleryImage}
            maxDate={
              new Date(
                today.getFullYear() - 18,
                today.getMonth(),
                today.getDate(),
              )
            }
            religionOptions={religionOptions}
            religionLoading={religion_loading}
            maritalStatusOptions={maritalStatusOptions}
            maritalStatusLoading={maritialstatus_loading}
            casteOptions={casteOptions}
            casteLoading={cast_loading}
            dobPickerOpen={showDobPicker}
            tobPickerOpen={showTobPicker}
            onDobPickerClose={() => setShowDobPicker(false)}
            onTobPickerClose={() => setShowTobPicker(false)}
            onDobPickerOpen={() => setShowDobPicker(true)}
            onTobPickerOpen={() => setShowTobPicker(true)}
          />
        );
      case 2:
        return (
          <Step2
            data={formData}
            onChange={handleChange}
            brothers={brothers}
            sisters={sisters}
            onBrotherChange={handleBrotherChange}
            onSisterChange={handleSisterChange}
            onDeleteBrother={handleDeleteBrother}
            onDeleteSister={handleDeleteSister}
            onBrotherCountChange={handleBrotherCountChange}
            onSisterCountChange={handleSisterCountChange}
          />
        );
      case 3:
        return (
          <Step3
            data={formData}
            onChange={handleChange}
            onMosalCountryChange={handleMosalCountryChange}
            onMosalStateChange={handleMosalStateChange}
            countryOptions={countryOptions}
            countryLoading={coutry_loading}
            mosalStateOptions={mosalStateOptions}
            mosalStateLoading={mosalStateLoading}
            mosalCityOptions={mosalCityOptions}
            mosalCityLoading={mosalCityLoading}
          />
        );
      case 4:
        return <Step4 data={formData} onChange={handleChange} />;
      case 5:
        return (
          <Step5
            data={formData}
            onChange={handleChange}
            onCountryChange={handleCountryChange}
            onStateChange={handleStateChange}
            countryOptions={countryOptions}
            countryLoading={coutry_loading}
            stateOptions={stateOptions}
            stateLoading={contactStateLoading}
            cityOptions={cityOptions}
            cityLoading={contactCityLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="ep-header">
        <button className="ep-header-back" onClick={goBack}>
          <FiArrowLeftCircle size={30} />
        </button>
        <h1>EDIT PROFILE</h1>
      </div>

      {profile_loading ? (
        <Loader />
      ) : (
        <div className="ep-layout">
          <aside className="ep-sidebar">
            <div className="ep-progress-bar">
              <div className="ep-progress-label">
                <span>Profile Completion</span>
                <span>{currentStep}/5</span>
              </div>
              <div className="ep-progress-track">
                <div
                  className="ep-progress-fill"
                  style={{ width: `${progressPct + 20}%` }}
                />
              </div>
            </div>
            <div className="ep-sidebar-title">Steps</div>
            <div className="ep-sidebar-steps">
              {STEPS.map((s) => (
                <div
                  key={s.id}
                  className={`ep-step ${s.cls}${currentStep === s.id ? " active" : ""}`}
                  onClick={() => setCurrentStep(s.id)}
                >
                  <div className="ep-step-icon">{s.icon}</div>
                  <div className="ep-step-info">
                    <div className="ep-step-label">{s.label}</div>
                    <div className="ep-step-sub">{s.sub}</div>
                  </div>
                  <div className="ep-step-num">{s.id}/5</div>
                </div>
              ))}
            </div>
          </aside>

          <main className="ep-main">
            <div className={`ep-step-header ${step.cls}`}>
              <div className="ep-step-header-icon">{step.icon}</div>
              <div>
                <div className="ep-step-header-title">{step.label}</div>
                <div className="ep-step-header-sub">{step.sub}</div>
              </div>
              <div className="ep-step-header-badge">{currentStep}/5</div>
            </div>

            {renderStep()}

            <div className={`ep-actions ${actionMode}`}>
              <div className="ep-actions-left">
                {currentStep > 1 && (
                  <button
                    type="button"
                    className="ep-btn-back"
                    onClick={goBack}
                  >
                    <FiArrowLeft /> Back
                  </button>
                )}
              </div>
              {currentStep < 5 ? (
                <button
                  type="button"
                  className={`ep-btn-continue ${step.cls}`}
                  onClick={goNext}
                >
                  Continue <FiArrowRight />
                </button>
              ) : (
                <button
                  type="button"
                  className={`ep-btn-continue ${step.cls}`}
                  onClick={handleFinalSubmit}
                  disabled={update_loading}
                >
                  {update_loading ? (
                    <Oval
                      height={20}
                      width={120}
                      color="#fff"
                      visible={true}
                      ariaLabel="oval-loading"
                      secondaryColor="#fff"
                      strokeWidth={5}
                      strokeWidthSecondary={5}
                    />
                  ) : (
                    <>
                      Save Changes <IoIosCheckmarkCircle size={18} />
                    </>
                  )}
                </button>
              )}
              {currentStep === 5 && !isProfileComplete && (
                <button
                  type="button"
                  className="ep-btn-logout ep-btn-logout-bottom"
                  onClick={handleLogout}
                >
                  <FiPower /> Logout
                </button>
              )}
            </div>
          </main>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
